/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { Button, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Portfolio } from '@g17eco/types/portfolio';
import { addHoldingToPortfolio } from '@actions/portfolio-tracker';
import { InitiativeSearch } from '@features/search/InitiativeSearch';
import { searchInitiativeHoldings } from '@actions/initiative';
import { Loader } from '@g17eco/atoms/loader';
import { useSelector } from 'react-redux';
import { getInitiativeGroupFlatTree } from '@selectors/initiativeTree';
import { InitiativePlain } from '@g17eco/types/initiative';
import { Suggestion } from '@g17eco/types/search';
import { getDefaultContent } from '@features/data-share/DataShareModal';
import { DataShareCreateBasic, RequesterType } from '@g17eco/types/dataShare';
import { getDefaultRequestDataScope } from '@utils/dataShare';
import { useSiteAlert } from '@hooks/useSiteAlert';
import { DataShareForm } from '@features/data-share/DataShareForm';
import { useCreateDataShareRequestMutation } from '@api/data-share';
import { AppCode } from '@g17eco/types/app';

const initialSuggestion = { _id: '', name: '', weight: 0, permissionGroup: '' };
enum AddCompanyStep {
  Search = 'search',
  RequestDataShare = 'requestDataShare',
}

const addParents = (itemId: string, initiativeTree: InitiativePlain[], filteredIds: string[]): void => {
  const item = initiativeTree.find((i) => i._id === itemId);
  if (!item || !item.parentId) {
    return;
  }

  filteredIds.push(item.parentId);
  addParents(item.parentId, initiativeTree, filteredIds);
};

interface AddCompanyModalProps {
  isOpen: boolean;
  toggle: () => void;
  portfolio: Portfolio;
  handleReload: () => void;
}

interface CompanySuggestion {
  _id: string;
  name: string;
  weight: number;
  appConfigCode?: AppCode;
}

export const AddCompanyModal = (props: AddCompanyModalProps) => {
  const { isOpen, toggle, portfolio, handleReload } = props;
  const { addSiteAlert, addSiteError } = useSiteAlert();
  const initiativeTree = useSelector(getInitiativeGroupFlatTree);

  const [step, setStep] = useState(AddCompanyStep.Search);
  const [suggestion, setSuggestion] = React.useState<CompanySuggestion>(initialSuggestion);
  const [isLoading, setLoading] = React.useState(false);

  const [createDataShareRequest] = useCreateDataShareRequestMutation();

  const defaultForm = {
    requesterId: portfolio._id,
    requesterType: RequesterType.Portfolio,
    initiativeId: '',
    title: '',
    content: getDefaultContent({ requesterName: portfolio.name }),
    dataScope: getDefaultRequestDataScope(),
  };
  const [form, setForm] = useState<DataShareCreateBasic>(defaultForm);

  useEffect(() => {
    setForm((preValue) => ({
      ...preValue,
      initiativeId: suggestion._id,
      title: `${portfolio.name} is requesting access to ${suggestion.name}`,
    }));
  }, [suggestion, portfolio.name]);

  const handleClose = () => {
    setSuggestion(initialSuggestion);
    setForm(defaultForm);
    setStep(AddCompanyStep.Search);
    toggle();
  };

  const handleSetHolding = (value: string, suggestion?: Suggestion) => {
    // Suggestion here is actually ExtendedCompanySuggestion
    // Therefore it should have appConfigCode
    // TODO: Fix type issues of this function and InitiativeSearch component
    if (suggestion && suggestion._id) {
      setSuggestion((prev) => ({ ...prev, ...suggestion }));
    } else {
      setSuggestion(initialSuggestion);
    }
  };

  const handleSubmit = async ({ withDataShareRequest }: { withDataShareRequest: boolean }) => {
    if (!suggestion._id || isLoading) {
      return;
    }

    setLoading(true);
    try {
      await addHoldingToPortfolio(props.portfolio._id, suggestion._id, suggestion.weight);

      if (!withDataShareRequest) {
        return;
      }

      await createDataShareRequest(form);
      addSiteAlert({
        content: `Your share request to ${suggestion.name} has been sent.`,
        timeout: 5000,
      });
    } catch (e) {
      addSiteError(e.message);
    } finally {
      handleReload();
      setLoading(false);
      handleClose();
    }
  };

  const onChange = (u: Partial<DataShareCreateBasic>) => setForm((f) => ({ ...f, ...u }));

  const filteredIds = [portfolio._id, ...portfolio.scorecard.children.map((c) => c.initiativeId)];
  addParents(portfolio._id, initiativeTree, filteredIds);

  if (isLoading) {
    return <Loader relative={true} />;
  }

  const renderBody = () => {
    if (step === AddCompanyStep.Search) {
      return (
        <div className='row'>
          <div className='col'>
            <div className='mb-1'>Search for a company by name</div>
            <div>
              <InitiativeSearch
                disabled={isLoading}
                placeholder={'Search for a company'}
                searchInitiative={searchInitiativeHoldings}
                filterOptions={filteredIds}
                handleValueChange={handleSetHolding}
              />
            </div>
            <div className='mt-3 mb-1'>Set a portfolio weighting for this company</div>
            <InputGroup>
              <Input
                type={'number'}
                min={0}
                max={100}
                defaultValue={0}
                onChange={(e) => setSuggestion((prev) => ({ ...prev, weight: Number(e.target.value) }))}
              />
              <InputGroupText>Percentage</InputGroupText>
            </InputGroup>
          </div>
        </div>
      );
    }

    if (step === AddCompanyStep.RequestDataShare) {
      return <DataShareForm form={form} onChange={onChange} disabled={isLoading} />;
    }

    return null;
  };

  const renderFooter = () => {
    if (step === AddCompanyStep.Search) {
      return (
        <>
          <Button color='link-secondary' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            className='ml-3'
            disabled={!suggestion._id}
            onClick={() => setStep(AddCompanyStep.RequestDataShare)}
          >
            Add
          </Button>
        </>
      );
    }

    if (step === AddCompanyStep.RequestDataShare) {
      return (
        <div className='w-100 d-flex justify-content-between'>
          <div className='flex-grow-1'>
            <Button color='link-secondary' onClick={handleClose}>
              Cancel
            </Button>
          </div>
          <div>
            <Button onClick={() => handleSubmit({ withDataShareRequest: false })}>Add without data share</Button>
            <Button
              color='primary'
              className='ml-3'
              disabled={!suggestion._id || isLoading || suggestion.appConfigCode === AppCode.MaterialityTracker }
              onClick={() => handleSubmit({ withDataShareRequest: true })}
            >
              Send
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} backdrop='static' className='modal-add-company'>
      <ModalHeader toggle={handleClose}>Add company</ModalHeader>
      <ModalBody>{renderBody()}</ModalBody>
      <ModalFooter>{renderFooter()}</ModalFooter>
    </Modal>
  );
};
